import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import mediaService from '../../services/mediaService';
import vibesService from '../../services/vibesService';
import { toast } from '../../utils/notify';
import VipIcon from '../Icons/VipIcons';
import LoadingSpinner, { InlineLoading } from '../LoadingSpinner/LoadingSpinner';
import { TextArea } from '../common/Input';
import { PrimaryButton, SecondaryButton, IconButton, LinkButton } from '../common/Button';
import VibeTrimmer from './VibeTrimmer';
import { VIBE } from '../../constants/staticTexts';

const MAX_VIDEO_SECONDS = 15;
// 5 GB — matches the S3 single-PUT ceiling. Backend enforces 10GB, and the
// admin settings can tighten this per-tier, but the composer should not
// prematurely reject anything the user's device can hand us.
const MAX_FILE_MB = 5 * 1024;

/**
 * Detect iOS (iPhone / iPad / iPod on any browser — every iOS browser uses
 * WebKit, so the constraints are the same). iPadOS 13+ reports as MacIntel
 * so we also check for touch to catch that shape.
 *
 * We use this to skip the in-browser VibeTrimmer on iOS because its two trim
 * paths both fail there:
 *   1. ffmpeg.wasm — the standard core can't decode HEVC (Photos' default),
 *      and iOS Safari's memory ceiling kills the WASM heap on longer clips.
 *   2. MediaRecorder fallback — depends on `video.captureStream()`, which
 *      no iOS browser implements.
 * Instead we send iOS users through the native Photos trimmer.
 */
const isIOSDevice = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return true;
    return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
};

/**
 * Probe a video File's duration by loading its metadata into an offscreen
 * <video>. Resolves with the duration in seconds, or null if the metadata
 * can't be read within a short timeout (some HEVC/PROV files stall).
 */
const probeVideoDurationSec = (file) => new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    let settled = false;
    const finish = (val) => {
        if (settled) return;
        settled = true;
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(val);
    };
    video.onloadedmetadata = () => {
        const d = Number.isFinite(video.duration) ? video.duration : null;
        finish(d);
    };
    video.onerror = () => finish(null);
    // Some codecs never fire loadedmetadata on iOS; cap the wait at 4s.
    setTimeout(() => finish(null), 4000);
    video.src = url;
});

/** Format a duration in seconds for display in the trim guidance modal. */
const formatDurationLabel = (sec) => {
    if (!Number.isFinite(sec) || sec <= 0) return '';
    if (sec < 60) return `${Math.round(sec)}s`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
};

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    z-index: 10000005;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

const Panel = styled.div`
    background: ${({ theme }) => theme.colors?.bg2 || '#1a1a1a'};
    color: ${({ theme }) => theme.colors?.text || '#e5e7eb'};
    border-radius: 16px;
    width: 100%;
    max-width: 360px;
    max-height: 92vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
    padding: 12px 12px 14px;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    padding: 0 2px;
`;

const Title = styled.h2`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
`;

const PreviewFrame = styled.div`
    width: 100%;
    aspect-ratio: 9 / 16;
    max-height: 60vh;
    border-radius: 12px;
    overflow: hidden;
    background: #000;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 10px;
    position: relative;

    img, video {
        width: 100%;
        height: 100%;
        object-fit: contain;
        position: relative;
        z-index: 1;
    }
`;

const PreviewBackdrop = styled.div`
    position: absolute;
    inset: -20px;
    background-image: ${({ $src }) => ($src ? `url(${$src})` : 'none')};
    background-size: cover;
    background-position: center;
    filter: blur(28px) brightness(0.55) saturate(1.2);
    transform: scale(1.15);
    z-index: 0;
    pointer-events: none;
`;

const PreviewBadge = styled.div`
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 3;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    gap: 3px;
`;

const PreviewPlaceholder = styled.div`
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: ${({ theme }) => theme.colors?.bg2 || '#1a1a1a'};
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
    font-size: 11px;
`;

const Row = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
    margin: 14px 0;
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
`;

const Hint = styled.div`
    font-size: 12px;
    color: ${({ theme }) => theme.colors?.textSecondary || '#9ca3af'};
    margin-top: 6px;
`;

const VibeComposer = ({ onCreated, onClose }) => {
    const inputRef = useRef(null);
    const previewUrlRef = useRef(null);
    // Guards state updates from stale uploads (user replaced the file mid-upload).
    const activeUploadId = useRef(0);

    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isVideo, setIsVideo] = useState(false);
    const [caption, setCaption] = useState('');

    // Upload lifecycle — runs on file pick, mirrors posts/messages.
    const [uploading, setUploading] = useState(false);
    const [uploadPct, setUploadPct] = useState(0);
    const [uploadError, setUploadError] = useState(null);
    const [uploadedMedia, setUploadedMedia] = useState(null); // { mediaFileId, s3Key }
    // URL returned by /media/process — preferred over the local blob preview
    // once available so the modal shows the same thumbnail the rail will render.
    const [processedPreviewUrl, setProcessedPreviewUrl] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    // Trimmer state — opened only when the picked video exceeds MAX_VIDEO_SECONDS.
    // While pendingTrimFile is set, no upload is kicked off; the trimmer decides
    // whether to hand a shorter File back for the normal upload path or bail.
    const [pendingTrimFile, setPendingTrimFile] = useState(null);
    // iOS guidance state — set when an iOS user picks a video that's longer
    // than MAX_VIDEO_SECONDS. We show a Photos-trim walkthrough instead of the
    // in-browser trimmer because neither trim path works on iOS Safari.
    const [iosTrimGuide, setIosTrimGuide] = useState(null); // { durationSec }

    const revokePreview = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
    }, []);

    const resetUploadState = () => {
        setUploading(false);
        setUploadPct(0);
        setUploadError(null);
        setUploadedMedia(null);
        setProcessedPreviewUrl(null);
    };

    const runUpload = async (f, uploadTicket) => {
        setUploading(true);
        setUploadPct(0);
        setUploadError(null);
        setUploadedMedia(null);
        try {
            const presign = await mediaService.getPresignedUploadUrl(
                f.name, f.type, 'post', f.size
            );
            if (activeUploadId.current !== uploadTicket) return;

            await mediaService.uploadToS3(
                presign.presignedUrl, f, f.type,
                (p) => {
                    if (activeUploadId.current !== uploadTicket) return;
                    setUploadPct(Math.round(p * 0.9));
                }
            );
            if (activeUploadId.current !== uploadTicket) return;
            setUploadPct(92);

            // Crawl 92 → 99 while /media/process is running so the striped
            // progress bar keeps moving instead of stalling at 92%.
            const processTick = setInterval(() => {
                if (activeUploadId.current !== uploadTicket) {
                    clearInterval(processTick);
                    return;
                }
                setUploadPct((prev) => (prev < 99 ? prev + 1 : prev));
            }, 250);

            try {
                const processResult = await mediaService.processMedia(presign.s3Key, 'post', presign.uploadId);
                if (activeUploadId.current !== uploadTicket) return;
                // Prefer the server-rendered thumbnail so we can drop the huge
                // local blob preview and keep the modal from scrolling.
                const thumbUrl = processResult?.thumbnails?.[0]?.url
                    || processResult?.thumbnails?.[0]?.cdnUrl
                    || null;
                if (thumbUrl) setProcessedPreviewUrl(thumbUrl);
            } catch (procErr) {
                // Processing is best-effort at pick time — the vibe still publishes
                // once media reaches APPROVED via the backend media completion path.
            } finally {
                clearInterval(processTick);
            }
            if (activeUploadId.current !== uploadTicket) return;

            setUploadPct(100);
            setUploadedMedia({ mediaFileId: presign.mediaFileId, s3Key: presign.s3Key });
        } catch (err) {
            if (activeUploadId.current !== uploadTicket) return;
            setUploadError(err?.response?.data?.message || err?.message || 'Upload failed');
            toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
        } finally {
            if (activeUploadId.current === uploadTicket) {
                setUploading(false);
            }
        }
    };

    const handleFile = async (f) => {
        if (!f) return;
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
            const gb = (MAX_FILE_MB / 1024).toFixed(0);
            toast.error(`File must be under ${gb} GB`);
            return;
        }
        const video = f.type.startsWith('video/');
        const image = f.type.startsWith('image/');
        if (!video && !image) {
            toast.error('Only image or video files allowed');
            return;
        }
        if (video) {
            // iOS has no working client trim path (see isIOSDevice comment).
            // Route iOS users through the native Photos trimmer instead.
            if (isIOSDevice()) {
                const durationSec = await probeVideoDurationSec(f);
                // Allow ~0.5s slop for floating-point + Photos snap-to-frame.
                if (durationSec == null || durationSec <= MAX_VIDEO_SECONDS + 0.5) {
                    acceptPickedFile(f, true);
                    return;
                }
                setIosTrimGuide({ durationSec });
                return;
            }
            // Non-iOS: always route through the trimmer so users can pick the
            // exact window they want, regardless of source length.
            setPendingTrimFile(f);
            return;
        }
        acceptPickedFile(f, video);
    };

    // Called from handleFile (short-enough clips) AND from the trimmer's onConfirm.
    const acceptPickedFile = (f, isVideoFile) => {
        revokePreview();
        const url = URL.createObjectURL(f);
        previewUrlRef.current = url;
        setFile(f);
        setPreviewUrl(url);
        setIsVideo(isVideoFile);

        // Invalidate any in-flight upload from a previously picked file, then
        // kick off presign + PUT + process immediately (parity with posts).
        const ticket = ++activeUploadId.current;
        resetUploadState();
        runUpload(f, ticket);
    };

    const handleTrimConfirm = (trimmedFile) => {
        setPendingTrimFile(null);
        acceptPickedFile(trimmedFile, true);
    };

    const handleTrimCancel = (errorMessage) => {
        setPendingTrimFile(null);
        if (errorMessage) toast.error(errorMessage);
        // Reset the <input> so the same file can be re-picked.
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleClose = () => {
        if (submitting) return;
        activeUploadId.current++; // invalidate any in-flight upload
        revokePreview();
        onClose?.();
    };

    // Open the OS file picker (used both on mount and from the fallback panel
    // when the user accidentally dismissed the first picker).
    const openPicker = useCallback(() => {
        const input = inputRef.current;
        if (!input) return;
        // Reset so re-picking the same file still fires 'change'.
        input.value = '';
        input.click();
    }, []);

    // Auto-open the OS file picker as soon as the composer mounts so the user
    // doesn't have to click through an intermediate dropzone. If they dismiss
    // the picker, the fallback panel below lets them re-open it — we no longer
    // auto-close the composer, since focus-based cancel detection is flaky.
    const autoOpenedRef = useRef(false);
    useEffect(() => {
        if (autoOpenedRef.current) return;
        autoOpenedRef.current = true;
        openPicker();
    }, [openPicker]);

    const handleRetryUpload = () => {
        if (!file) return;
        const ticket = ++activeUploadId.current;
        runUpload(file, ticket);
    };

    const handleSubmit = async () => {
        if (!file || submitting || uploading || !uploadedMedia) return;
        setSubmitting(true);
        try {
            await vibesService.createVibe({
                mediaFileId: uploadedMedia.mediaFileId,
                caption: caption.trim() || null,
            });
            toast.success('Vibe shared!');
            revokePreview();
            onCreated?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || err?.message || 'Could not share vibe');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {pendingTrimFile && (
                <VibeTrimmer
                    file={pendingTrimFile}
                    maxSeconds={MAX_VIDEO_SECONDS}
                    onConfirm={handleTrimConfirm}
                    onCancel={handleTrimCancel}
                />
            )}
            {iosTrimGuide && ReactDOM.createPortal(
                <Overlay onClick={() => setIosTrimGuide(null)}>
                    <Panel onClick={(e) => e.stopPropagation()}>
                        <HeaderRow>
                            <Title>{VIBE.VIBE_TRIM_IOS_TITLE}</Title>
                            <IconButton $size="small" onClick={() => setIosTrimGuide(null)} aria-label="Close">
                                <VipIcon name="CloseXCircle" size={22} />
                            </IconButton>
                        </HeaderRow>
                        <div style={{ padding: '4px 4px 12px', fontSize: 14, lineHeight: 1.5 }}>
                            <p style={{ margin: '0 0 8px' }}>
                                Your clip is{' '}
                                <strong>{formatDurationLabel(iosTrimGuide.durationSec)}</strong>.{' '}
                                Vibes are limited to <strong>{MAX_VIDEO_SECONDS} seconds</strong>.
                            </p>
                            <p style={{ margin: 0 }}>
                                Please trim the video on your device first, then pick it again.
                            </p>
                        </div>
                        <ActionRow>
                            <SecondaryButton
                                type="button"
                                onClick={() => setIosTrimGuide(null)}
                            >
                                {VIBE.VIBE_CANCEL}
                            </SecondaryButton>
                            <PrimaryButton
                                type="button"
                                onClick={() => {
                                    setIosTrimGuide(null);
                                    openPicker();
                                }}
                            >
                                {VIBE.VIBE_TRIM_IOS_PICK_ANOTHER}
                            </PrimaryButton>
                        </ActionRow>
                    </Panel>
                </Overlay>,
                document.body
            )}
            {/* Hidden input rendered outside the panel so it stays mounted
                for programmatic click() on mount and for the retry path. */}
            <input
                ref={inputRef}
                type="file"
                accept="image/*,video/*"
                hidden
                onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {previewUrl ? ReactDOM.createPortal(
                <Overlay onClick={handleClose}>
                    <Panel onClick={(e) => e.stopPropagation()}>
                        <HeaderRow>
                            <Title>{VIBE.VIBE_SHARE}</Title>
                            <IconButton $size="small" onClick={handleClose} aria-label="Close">
                                <VipIcon name="CloseXCircle" size={22} />
                            </IconButton>
                        </HeaderRow>

                        <PreviewFrame>
                            <PreviewBackdrop
                                $src={
                                    processedPreviewUrl
                                        || (!isVideo ? previewUrl : null)
                                }
                            />
                            {processedPreviewUrl ? (
                                <>
                                    <img src={processedPreviewUrl} alt="preview" />
                                    {isVideo && (
                                        <PreviewBadge>
                                            <VipIcon name="Play" size={10} /> {VIBE.VIBE_VIDEO}
                                        </PreviewBadge>
                                    )}
                                </>
                            ) : (
                                <PreviewPlaceholder>
                                    <LoadingSpinner
                                        variant="logo"
                                        size={40}
                                        showText={false}
                                    />
                                    <div>{VIBE.VIBE_PREVIEW_IN_PROGRESS}</div>
                                </PreviewPlaceholder>
                            )}
                        </PreviewFrame>

                        <TextArea
                            placeholder="Add a caption (optional)"
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            maxLength={280}
                            disabled={submitting}
                        />

                        {uploadError && (
                            <Hint style={{ color: '#ef4444' }}>
                                {uploadError}{' '}
                                <LinkButton
                                    type="button"
                                    onClick={handleRetryUpload}
                                >
                                    {VIBE.VIBE_RETRY}
                                </LinkButton>
                            </Hint>
                        )}

                        <ActionRow>
                            <SecondaryButton type="button" onClick={handleClose} disabled={submitting}>
                                {VIBE.VIBE_CANCEL}
                            </SecondaryButton>
                            <PrimaryButton
                                type="button"
                                onClick={handleSubmit}
                                disabled={submitting || uploading || !uploadedMedia}
                            >
                                {(submitting || uploading) && (
                                    <InlineLoading size={16} color="#ffffff" />
                                )}
                                <span>
                                    {submitting ? VIBE.VIBE_SHARING : uploading ? VIBE.VIBE_UPLOADING : VIBE.VIBE_SHARE}
                                </span>
                            </PrimaryButton>
                        </ActionRow>
                    </Panel>
                </Overlay>,
                document.body
            ) : (
                // No file picked yet — show a compact panel with a "Choose"
                // button so users can re-open the picker if the auto-open
                // was dismissed. Hidden while the trimmer or iOS guidance is up.
                !pendingTrimFile && !iosTrimGuide && ReactDOM.createPortal(
                    <Overlay onClick={handleClose}>
                        <Panel onClick={(e) => e.stopPropagation()}>
                            <HeaderRow>
                                <Title>{VIBE.VIBE_SHARE}</Title>
                                <IconButton $size="small" onClick={handleClose} aria-label="Close">
                                    <VipIcon name="CloseXCircle" size={22} />
                                </IconButton>
                            </HeaderRow>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 12,
                                padding: '32px 16px',
                                textAlign: 'center',
                            }}>
                                <VipIcon name="Plus" size={32} />
                                <div style={{ fontSize: 14, fontWeight: 500 }}>
                                    {VIBE.VIBE_PICK_TITLE}
                                </div>
                                <Hint style={{ margin: 0 }}>
                                    {VIBE.VIBE_CREATION_HINT}
                                </Hint>
                                <PrimaryButton
                                    type="button"
                                    onClick={openPicker}
                                    style={{ marginTop: 8 }}
                                >
                                    {VIBE.VIBE_CHOOSE_FROM_DEVICE}
                                </PrimaryButton>
                            </div>
                        </Panel>
                    </Overlay>,
                    document.body
                )
            )}
        </>
    );
};

export default VibeComposer;
